import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { commentService } from "@/features/announcements/services/commentService";
import {
  translateText,
  SUPPORTED_LANGUAGES,
  LANGUAGE_FLAGS,
  LANGUAGE_NATIVE_NAMES,
  type LanguageCode,
} from "@/features/chat/services/translationService";
import { Button } from "@/shared/components/ui/Button";
import { Spinner } from "@/shared/components/ui/Spinner";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";
import { formatDateTime } from "@/shared/utils/formatters";
import type { Comment } from "@/features/announcements/types/feed";

interface CommentSectionProps {
  announcementId: number | string;
  relationType?: string;
  currentUserId: string | number;
  currentUserName?: string;
  isOfficer: boolean;
  onCountChange?: (count: number) => void;
  readOnly?: boolean;
}

export const CommentSection = ({
  announcementId,
  relationType = "announcements",
  currentUserId,
  currentUserName,
  isOfficer,
  onCountChange,
  readOnly = false,
}: CommentSectionProps) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  // Translation state
  const [translations, setTranslations] = useState<
    Record<string | number, string>
  >({});
  const [translatingId, setTranslatingId] = useState<string | number | null>(
    null,
  );
  // Language picker: which comment's picker is open
  const [pickerOpenFor, setPickerOpenFor] = useState<string | number | null>(
    null,
  );
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    if (pickerOpenFor === null) return;
    const handleClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpenFor(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [pickerOpenFor]);

  const loadComments = async () => {
    try {
      const data = await commentService.getComments(
        relationType,
        announcementId,
      );
      const list = data ?? [];
      setComments(list);
      onCountChange?.(list.length);
    } catch (err) {
      console.error("Failed to load comments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [announcementId]);

  // ── Add comment ──
  const handleAdd = async () => {
    if (!newText.trim()) return;
    setSubmitting(true);
    try {
      await commentService.addComment({
        relation_type: relationType,
        relation_id: announcementId,
        content_text: newText.trim(),
        author_name: currentUserName,
      });
      setNewText("");
      await loadComments();
    } catch (err) {
      console.error("Failed to add comment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Edit comment (own only) ──
  const startEdit = (comment: Comment) => {
    setEditingId(comment.comment_id);
    setEditText(comment.content_text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const saveEdit = async (commentId: string | number) => {
    if (!editText.trim()) return;
    setSavingEdit(true);
    try {
      await commentService.updateComment(commentId, editText.trim());
      setEditingId(null);
      setEditText("");
      setTranslations((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
      await loadComments();
    } catch (err) {
      console.error("Failed to update comment:", err);
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Delete comment ──
  const handleDelete = async (commentId: string | number) => {
    setDeletingId(commentId);
    try {
      await commentService.deleteComment(commentId);
      setTranslations((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
      await loadComments();
    } catch (err) {
      console.error("Failed to delete comment:", err);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Translate a comment to a chosen language ──
  const handleTranslate = async (
    comment: Comment,
    targetLang: LanguageCode,
  ) => {
    setPickerOpenFor(null);
    setTranslatingId(comment.comment_id);
    try {
      const result = await translateText(comment.content_text, targetLang);
      if (result.success) {
        setTranslations((prev) => ({
          ...prev,
          [comment.comment_id]: result.translatedText,
        }));
      } else {
        console.error("Translation failed:", result.error);
      }
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setTranslatingId(null);
    }
  };

  const showOriginal = (commentId: string | number) => {
    setTranslations((prev) => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
  };

  const isOwner = (comment: Comment) =>
    String(comment.author_id) === String(currentUserId);

  if (loading) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <p className="text-sm font-medium text-gray-700 mb-3">
        {t("feed.comments")} ({comments.length})
      </p>

      {/* ── Comment list ── */}
      {comments.length === 0 ? (
        <p className="text-xs text-gray-400 italic mb-3">
          {t("feed.no_comments")}
        </p>
      ) : (
        <div className="space-y-3 mb-4">
          {comments.map((comment) => {
            const isTranslated = !!translations[comment.comment_id];
            const isTranslating = translatingId === comment.comment_id;
            const isPickerOpen = pickerOpenFor === comment.comment_id;

            return (
              <div
                key={comment.comment_id}
                className="group flex gap-3 text-sm"
              >
                {/* Avatar */}
                <div className="shrink-0 mt-0.5">
                  <ProfileAvatar
                    picId={comment.author_profile_pic_id}
                    firstName={(comment.author_name || "").split(" ")[0]}
                    lastName={(comment.author_name || "").split(" ").slice(1).join(" ")}
                    size="sm"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Name + timestamp */}
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-gray-900 text-xs">
                      {comment.author_name || t("feed.unknown_author")}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {formatDateTime(comment.created_at)}
                      {comment.updated_at &&
                        comment.updated_at !== comment.created_at && (
                          <span className="ml-1 italic">
                            ({t("feed.edited")})
                          </span>
                        )}
                    </span>
                  </div>

                  {/* Body or edit input */}
                  {editingId === comment.comment_id ? (
                    <div className="mt-1">
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <div className="flex gap-2 mt-1">
                        <Button
                          size="sm"
                          onClick={() => saveEdit(comment.comment_id)}
                          loading={savingEdit}
                        >
                          {t("common.save")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelEdit}
                        >
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-700 mt-0.5 whitespace-pre-wrap text-xs leading-relaxed">
                        {isTranslated
                          ? translations[comment.comment_id]
                          : comment.content_text}
                      </p>
                      {isTranslated && (
                        <p className="text-[10px] text-indigo-400 italic mt-0.5">
                          {t("feed.translated")}
                        </p>
                      )}
                    </>
                  )}

                  {/* Actions — always visible on mobile, hover on desktop */}
                  {editingId !== comment.comment_id && (
                    <div className="flex gap-3 md:gap-2 mt-1.5 md:mt-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      {/* Translate / Show Original */}
                      <div className="relative">
                        {isTranslated ? (
                          <button
                            onClick={() => showOriginal(comment.comment_id)}
                            className="text-xs md:text-[10px] py-1 text-gray-400 hover:text-indigo-600 active:text-indigo-700 transition-colors"
                          >
                            {t("feed.show_original")}
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              setPickerOpenFor(
                                isPickerOpen ? null : comment.comment_id,
                              )
                            }
                            disabled={isTranslating}
                            className="text-xs md:text-[10px] py-1 text-gray-400 hover:text-indigo-600 active:text-indigo-700 transition-colors disabled:opacity-50"
                          >
                            {isTranslating
                              ? t("feed.translating")
                              : t("feed.translate")}
                          </button>
                        )}

                        {/* Language picker popup */}
                        {isPickerOpen && (
                          <div
                            ref={pickerRef}
                            className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[200px] py-1 overflow-y-auto max-h-[60vh]"
                          >
                            {SUPPORTED_LANGUAGES.map((lang) => (
                              <button
                                key={lang.code}
                                onClick={() =>
                                  handleTranslate(
                                    comment,
                                    lang.code as LanguageCode,
                                  )
                                }
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <span className="text-base">
                                  {LANGUAGE_FLAGS[lang.code] || ""}
                                </span>
                                <span>
                                  {LANGUAGE_NATIVE_NAMES[lang.code] ||
                                    lang.name}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Edit — only comment owner */}
                      {!readOnly && isOwner(comment) && (
                        <button
                          onClick={() => startEdit(comment)}
                          className="text-[10px] text-gray-400 hover:text-indigo-600 transition-colors"
                        >
                          {t("common.edit")}
                        </button>
                      )}

                      {/* Delete — officer can delete any, poster can delete own */}
                      {!readOnly && (isOfficer || isOwner(comment)) && (
                        <button
                          onClick={() => handleDelete(comment.comment_id)}
                          disabled={deletingId === comment.comment_id}
                          className="text-xs md:text-[10px] py-1 text-gray-400 hover:text-red-600 active:text-red-700 transition-colors disabled:opacity-50"
                        >
                          {deletingId === comment.comment_id
                            ? t("common.deleting")
                            : t("common.delete")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── New comment input ── */}
      {!readOnly && (
        <div className="flex gap-2 items-end">
          <textarea
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder={t("feed.add_comment")}
            rows={1}
            style={{ maxHeight: "6rem" }}
            className="flex-1 px-3 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none overflow-y-auto"
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = "auto";
              el.style.height = Math.min(el.scrollHeight, 96) + "px";
            }}
          />
          <Button
            size="sm"
            onClick={handleAdd}
            loading={submitting}
            disabled={!newText.trim()}
          >
            {t("common.send")}
          </Button>
        </div>
      )}
    </div>
  );
};
